import { Container } from "layouts";
import { Link } from "react-router-dom";
// Removed HeroSlider import - using static mockup image instead
import PaddingX from "layouts/PaddingX";
import FeaturedBooks from "./FeaturedBooks";
import SpotlightCarousel from "components/v2/SpotlightCarousel";
import useGetSpotlightedBooks from "api/BookResource/useGetSpotlightedBooks";
import useGetLandingPageStats from "api/Statistics/useGetLandingPageStats";
import { BsDownload } from "react-icons/bs";

const Home = () => {
	const { data } = useGetLandingPageStats();
	const { data: spotlightData } = useGetSpotlightedBooks();
	const totalBooks = data?.total_active_books || 0;
	const totalRegisteredUsers = data?.total_registered_users || 0;
	const hasSpotlightedBooks = spotlightData?.data?.length > 0;

	return (
		<Container className="flex flex-col min-h-screen w-full">
			{/* Hero Section */}
			<section className="relative bg-gradient-to-br from-akgreener via-akgreenest to-akgreener text-white py-4 overflow-hidden">
				{/* Background decoration */}
				<div className="absolute inset-0 bg-black opacity-10"></div>
				<div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 opacity-10 rounded-full -translate-y-48 translate-x-48"></div>
				<div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full translate-y-32 -translate-x-32"></div>

				<PaddingX>
					<div className="max-w-7xl mx-auto relative z-10">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
							<div className="space-y-6">
								<div className="space-y-6">
									<div className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-medium backdrop-blur-sm">
										<span className="w-2 h-2 bg-yellow-300 rounded-full mr-2 animate-pulse"></span>
										Over {totalBooks} books available
									</div>
									<h1 className="text-4xl lg:text-5xl font-cera-black leading-tight">
										Unlock Your
										<br />
										<span className="text-yellow-300 relative">
											Potential
											<div className="absolute -bottom-2 left-0 w-full h-1 bg-yellow-300 transform -skew-x-12"></div>
										</span>
									</h1>
									<p className="text-lg lg:text-xl opacity-90 leading-relaxed max-w-lg">
										Discover, learn, and grow with our vast collection of academic and leisure
										books. Your journey to knowledge starts here.
									</p>
								</div>

								<div className="flex flex-col sm:flex-row gap-4">
									<Link
										to="/books"
										className="group bg-white text-akgreener flex items-center justify-center px-8 py-3 rounded-full font-bold text-base hover:bg-yellow-300 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
									>
										<span>Explore Library</span>
										<svg
											className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M13 7l5 5m0 0l-5 5m5-5H6"
											/>
										</svg>
									</Link>
									<Link
										to="/downloads"
										className="group border-2 border-white flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold text-base hover:bg-white hover:text-akgreener hover:scale-105 transition-all duration-300 backdrop-blur-sm"
									>
										<span>Download App</span>
										<BsDownload />
									</Link>
								</div>

								{/* Trust indicators */}
								<div className="flex items-center space-x-8 pt-4">
									<div className="text-center">
										<div className="text-2xl font-bold text-yellow-300">{totalBooks}+</div>
										<div className="text-sm opacity-75">Books</div>
									</div>
									<div className="text-center">
										<div className="text-2xl font-bold text-yellow-300">
											{totalRegisteredUsers}+
										</div>
										<div className="text-sm opacity-75">Registered users</div>
									</div>
								</div>
							</div>

							<div className="hidden lg:block relative">
								{hasSpotlightedBooks ? (
									<div className="relative z-10">
										<SpotlightCarousel mini={true} />
									</div>
								) : (
									<div className="relative flex items-center justify-center ">
										<img
											src="/images/mockup2.png"
											alt="Mobile app mockup showcasing the library interface"
											className="transition-transform duration-700 ease-in-out hover:scale-105 transform -translate-y-10 "
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
												objectPosition: "center",
											}}
										/>
										{/* Floating elements for visual appeal */}
										<div className="absolute -top-4 -right-4 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
										<div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white rounded-full opacity-30 animate-pulse"></div>
									</div>
								)}
							</div>
						</div>
					</div>
				</PaddingX>
			</section>

			{/* Why Choose Us Section */}
			<section className="py-16 bg-gray-50">
				<PaddingX>
					<div className="max-w-7xl mx-auto">
						<div className="text-center mb-16">
							<h2 className="text-3xl lg:text-4xl font-cera-black text-gray-700 mb-6">
								Why Choose <span className="text-akgreener">Our Library</span>?
							</h2>
							<p className="text-lg text-gray-600 max-w-3xl mx-auto">
								Browse and download books from our website to our mobile app for the ultimate
								reading experience. Your perfect reading journey starts here.
							</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
							{/* Feature 1 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">Vast Collection</h3>
								<p className="text-gray-600 leading-relaxed">
									Browse over {totalBooks}+ carefully curated books spanning academic subjects,
									fiction, non-fiction, and specialized topics. Download to our mobile app for
									reading.
								</p>
							</div>

							{/* Feature 2 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 10V3L4 14h7v7l9-11h-7z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">Instant Download</h3>
								<p className="text-gray-600 leading-relaxed">
									Download books instantly to our mobile app and start reading immediately. No
									waiting, no delays - your next great read is just one click away.
								</p>
							</div>

							{/* Feature 3 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">Secure & Private</h3>
								<p className="text-gray-600 leading-relaxed">
									Your reading data and personal information are protected with enterprise-grade
									security. Read with confidence and privacy.
								</p>
							</div>

							{/* Feature 4 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">Mobile-First Reading</h3>
								<p className="text-gray-600 leading-relaxed">
									Download books from our website to our mobile app for the best reading experience.
									Your reading progress, bookmarks, and notes sync automatically across all your
									devices.
								</p>
							</div>

							{/* Feature 5 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">Affordable Pricing</h3>
								<p className="text-gray-600 leading-relaxed">
									Access thousands of books at a fraction of the cost of buying individual titles.
									Premium content at budget-friendly prices.
								</p>
							</div>

							{/* Feature 6 */}
							<div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
								<div className="w-16 h-16 bg-akgreener bg-opacity-10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
									<svg
										className="w-8 h-8 text-akgreener"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Support</h3>
								<p className="text-gray-600 leading-relaxed">
									Get help whenever you need it with our round-the-clock customer support. We're
									here to ensure your reading experience is smooth.
								</p>
							</div>
						</div>
					</div>
				</PaddingX>
			</section>

			{/* Features Section */}
			<section className="py-20">
				<PaddingX>
					<FeaturedBooks />
				</PaddingX>
			</section>
		</Container>
	);
};

export default Home;
